import { useIntl } from '@umijs/max';
import {
  Alert,
  Button,
  Empty,
  Input,
  Progress,
  Segmented,
  Select,
  Spin,
  Table,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createStyles } from 'antd-style';
import dayjs from 'dayjs';
import React from 'react';
import CopyText from '@/components/CopyText';
import { ThunderboltOutlined } from '@/components/icon/tabler';
import { useFillTable, useUserMap } from '@/hooks';
import {
  type BusinessTask,
  type BusinessTaskDetailResult,
  type BusinessTaskKind,
  type BusinessTaskRequest,
  type BusinessTaskStatus,
  getAdminTaskDetail,
  listAdminTasks,
} from '@/services/drama/api';
import { EmptyDash } from '@/shared/components';
import { TouchSafeTooltip as Tooltip } from '@/shared/components/TouchSafeTooltip';
import TraceDrawer from './TraceDrawer';

const ALL_KINDS: BusinessTaskKind[] = [
  'parse-project', 'adapt-novel', 'extract-characters', 'extract-scenes', 'gen-storyboard',
  'canvas-image', 'canvas-video', 'canvas-audio', 'reverse-prompt-image', 'reverse-prompt-video',
];

const ALL_STATUSES: BusinessTaskStatus[] = [
  'queued',
  'processing',
  'completed',
  'partial',
  'failed',
  'canceled',
];

function formatElapsed(ms: number): string {
  if (!ms || ms <= 0) return '-';
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m${rem.toString().padStart(2, '0')}s`;
}

function formatTime(iso?: string) {
  if (!iso) return '-';
  return dayjs(iso).format('MM-DD HH:mm:ss');
}

function ModelProviderCell({
  modelId,
  providerName,
}: {
  modelId?: string;
  providerName?: string;
}) {
  if (!modelId && !providerName) {
    return <EmptyDash />;
  }
  return (
    <div className="task-model-provider-cell">
      {modelId && (
        <CopyText className="task-model-id" text={modelId}>
          {modelId}
        </CopyText>
      )}
      {providerName && (
        <div className="task-provider-name" title={providerName}>
          {providerName}
        </div>
      )}
    </div>
  );
}

const useStyles = createStyles(({ token }) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  toolbarLabel: {
    fontSize: 12,
    color: token.colorTextSecondary,
  },
  toolbarCounts: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginLeft: 'auto',

    '& .ant-tag': {
      marginInlineEnd: 0,
    },
  },
  taskIdText: {
    fontSize: 11,
    fontFamily: token.fontFamilyCode,
    color: token.colorTextTertiary,
  },
  thumbCell: {
    width: 32,
    height: 32,
    objectFit: 'cover' as const,
    borderRadius: token.borderRadiusSM,
    background: token.colorFillSecondary,
  },
  errorText: {
    fontSize: 11,
    color: token.colorErrorText,
    fontFamily: token.fontFamilyCode,
    wordBreak: 'break-all',
  },
  compactTable: {
    '& .ant-table-cell': {
      padding: '6px 8px !important',
      verticalAlign: 'middle',
    },
    '& .ant-table-thead > tr > th': {
      padding: '6px 8px !important',
      fontSize: 12,
    },
    '& .ant-table-expanded-row > .ant-table-cell': {
      padding: '0 !important',
      background: token.colorFillQuaternary,
    },
    '& .ant-table-expanded-row .ant-table-wrapper': {
      padding: '0 52px 0 52px',
    },
  },
  targetCell: {
    minWidth: 0,
  },
  targetTitle: {
    fontSize: 12,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  targetSubTitle: {
    fontSize: 11,
    color: token.colorTextTertiary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  modelProviderCell: {
    fontSize: 11,
    lineHeight: 1.45,
    minWidth: 0,

    '& .task-model-id': {
      display: 'block',
      maxWidth: '100%',
      fontFamily: token.fontFamilyCode,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    '& .task-provider-name': {
      maxWidth: '100%',
      color: token.colorTextSecondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },

  userLink: {
    cursor: 'pointer',
    color: token.colorPrimary,
    background: 'none',
    border: 'none',
    padding: 0,
    textAlign: 'left' as const,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  userEmailSub: {
    fontSize: 10,
    color: token.colorTextTertiary,
  },
}));

export interface TaskListPanelProps {
  scopedUserId?: string;

  showUserColumn?: boolean;

  tableScrollY?: number | string;

  nested?: boolean;

  onOpenUser?: (numericUserId: number) => void;
}

const TaskListPanel: React.FC<TaskListPanelProps> = ({
  scopedUserId,
  showUserColumn,
  tableScrollY,
  nested,
  onOpenUser,
}) => {
  const { styles } = useStyles();
  const intl = useIntl();

  const showUser = showUserColumn ?? !scopedUserId;

  const [loading, setLoading] = React.useState(false);
  const [tasks, setTasks] = React.useState<BusinessTask[]>([]);
  const [counts, setCounts] = React.useState<{
    queued?: number;
    processing: number;
    completed: number;
    partial: number;
    failed: number;
    total: number;
  }>({ processing: 0, completed: 0, partial: 0, failed: 0, total: 0 });
  const [error, setError] = React.useState<string | null>(null);
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: nested ? 10 : 50,
    total: nested ? 10 : 50,
    totalCapped: false,
  });

  const [userIdFilter, setUserIdFilter] = React.useState('');
  const [taskIdFilter, setTaskIdFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<
    BusinessTaskStatus | 'all'
  >('all');
  const [kindFilter, setKindFilter] = React.useState<BusinessTaskKind | 'all'>(
    'all',
  );

  const [detailMap, setDetailMap] = React.useState<
    Record<string, BusinessTaskDetailResult>
  >({});
  const [detailLoading, setDetailLoading] = React.useState<
    Record<string, boolean>
  >({});
  const [detailError, setDetailError] = React.useState<Record<string, string>>(
    {},
  );
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<string[]>([]);

  const [traceTask, setTraceTask] = React.useState<BusinessTask | null>(null);

  const taskUserIds = React.useMemo(
    () => (showUser ? tasks.map((t) => t.user_id || '').filter(Boolean) : []),
    [tasks, showUser],
  );
  const { userMap } = useUserMap(taskUserIds);

  // 页面级(非 Modal 嵌套、未显式给高度)时让表格填满视口、行少不塌
  const fillTable = !nested && tableScrollY === undefined;
  const { scrollY: fillScrollY, wrapperProps } = useFillTable(tasks.length);
  const effectiveScrollY =
    tableScrollY ?? (fillTable ? fillScrollY : undefined);

  const fetchList = React.useCallback(
    async (pageArg = pagination.current, pageSizeArg = pagination.pageSize) => {
      setLoading(true);
      setError(null);
      try {
        const effectiveUid = scopedUserId || userIdFilter.trim();
        const res = await listAdminTasks({
          userId: effectiveUid || undefined,
          taskId: taskIdFilter.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          kind: kindFilter === 'all' ? undefined : kindFilter,
          page: pageArg,
          pageSize: pageSizeArg,
          windowHours: 0,
        });
        if (res?.success && res.data) {
          const nextTasks = res.data.tasks || [];
          const pageInfo = res.data.pagination;
          const current = pageInfo?.page || pageArg;
          const pageSize = pageInfo?.page_size || pageSizeArg;
          // 后端返精确总数(admin 视图);老路径无 total 时回落 has_more 估算
          const total =
            typeof pageInfo?.total === 'number'
              ? pageInfo.total
              : pageInfo?.has_more
                ? current * pageSize + 1
                : (current - 1) * pageSize + nextTasks.length;
          const totalCapped =
            typeof pageInfo?.total === 'number'
              ? !!pageInfo.total_capped
              : !!pageInfo?.has_more;
          setTasks(nextTasks);
          setPagination({
            current,
            pageSize,
            total: Math.max(total, 0),
            totalCapped,
          });
          setCounts(
            res.data.counts || {
              processing: 0,
              completed: 0,
              partial: 0,
              failed: 0,
              total: 0,
            },
          );
        } else {
          setError(res?.error?.code || res?.error?.message || 'unknown');
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'request failed');
      } finally {
        setLoading(false);
      }
    },
    [
      scopedUserId,
      userIdFilter,
      taskIdFilter,
      statusFilter,
      kindFilter,
      pagination.current,
      pagination.pageSize,
    ],
  );

  React.useEffect(() => {
    fetchList(pagination.current, pagination.pageSize);

    setExpandedRowKeys([]);
    setDetailMap({});
    setDetailError({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scopedUserId,
    statusFilter,
    kindFilter,
    pagination.current,
    pagination.pageSize,
  ]);

  const fetchDetail = React.useCallback(async (task: BusinessTask) => {
    const id = task.id;
    const targetId = task.target_id || task.project_id;
    if (!targetId) return;
    setDetailLoading((m) => ({ ...m, [id]: true }));
    setDetailError((m) => {
      const next = { ...m };
      delete next[id];
      return next;
    });
    try {
      const res = await getAdminTaskDetail(task.kind, targetId, task.user_id);
      if (res?.success && res.data) {
        const data = res.data;
        setDetailMap((m) => ({ ...m, [id]: data }));
      } else {
        setDetailError((m) => ({
          ...m,
          [id]: res?.error?.code || 'unknown',
        }));
      }
    } catch (e: unknown) {
      setDetailError((m) => ({
        ...m,
        [id]: e instanceof Error ? e.message : 'request failed',
      }));
    } finally {
      setDetailLoading((m) => ({ ...m, [id]: false }));
    }
  }, []);

  const columns: ColumnsType<BusinessTask> = [
    {
      title: 'Task ID',
      dataIndex: 'id',
      width: 110,
      render: (id: string) => (
        <CopyText className={styles.taskIdText} text={id}>
          {id.slice(-10)}
        </CopyText>
      ),
    },
    {
      title: '调用链',
      key: 'trace',
      width: 70,
      render: (_: unknown, t: BusinessTask) => (
        <Button type="link" size="small" onClick={() => setTraceTask(t)}>
          查看
        </Button>
      ),
    },
    {
      title: intl.formatMessage({ id: 'monitor.tasks.modal.colKind' }),
      dataIndex: 'kind',
      width: 100,
      render: (kind: BusinessTaskKind, t: BusinessTask) => {
        const sub = t.sub_kind?.trim();
        const label = sub
          ? intl.formatMessage({
              id: `monitor.tasks.subkind.${sub}`,
              defaultMessage: sub,
            })
          : intl.formatMessage({ id: `monitor.tasks.kind.${kind}` });
        return <Tag>{label}</Tag>;
      },
    },
    ...(showUser
      ? [
          {
            title: intl.formatMessage({
              id: 'pages.adminTasks.colUser',
              defaultMessage: '用户',
            }),
            dataIndex: 'user_id',
            width: 150,
            render: (uid?: string) => {
              if (!uid) {
                return (
                  <span style={{ color: 'var(--ant-color-text-tertiary)' }}>
                    -
                  </span>
                );
              }
              const u = userMap[uid];
              const display = u?.profile?.nickname || u?.email;
              const email = u?.email;

              if (u && display) {
                if (nested) {
                  return (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>
                        {display}
                      </div>
                      {email && (
                        <div className={styles.userEmailSub}>{email}</div>
                      )}
                    </div>
                  );
                }
                return (
                  <button
                    type="button"
                    className={styles.userLink}
                    onClick={() => onOpenUser?.(u.userId)}
                  >
                    <div style={{ fontSize: 12, fontWeight: 500 }}>
                      {display}
                    </div>
                    {email && (
                      <div className={styles.userEmailSub}>{email}</div>
                    )}
                  </button>
                );
              }

              return (
                <CopyText
                  style={{ fontSize: 11, fontFamily: 'monospace' }}
                  text={uid}
                >
                  {uid.slice(-8)}
                </CopyText>
              );
            },
          },
        ]
      : []),
    {
      title: intl.formatMessage({ id: 'monitor.tasks.modal.colTarget' }),
      key: 'target',
      width: 420,
      ellipsis: true,
      render: (_: unknown, t: BusinessTask) => (
        <div className={styles.targetCell}>
          <div className={styles.targetTitle} title={t.target_label || '-'}>
            {t.target_label || '-'}
          </div>
          {t.project_title && (
            <div className={styles.targetSubTitle} title={t.project_title}>
              {t.project_title}
            </div>
          )}
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'monitor.tasks.modal.colStatus' }),
      dataIndex: 'status',
      width: 80,
      render: (s: BusinessTaskStatus, t: BusinessTask) => {
        const tag = (
          <Tag>{intl.formatMessage({ id: `monitor.tasks.status.${s}` })}</Tag>
        );
        if (!t.error) return tag;
        return (
          <Tooltip
            title={
              <span style={{ wordBreak: 'break-all', fontSize: 12 }}>
                {t.error}
              </span>
            }
            styles={{ root: { maxWidth: 480 } }}
          >
            <span style={{ cursor: 'help' }}>{tag}</span>
          </Tooltip>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'monitor.tasks.modal.colProgress' }),
      key: 'progress',
      width: 100,
      render: (_: unknown, t: BusinessTask) => {
        if (!t.progress || t.progress.total <= 0) {
          return <EmptyDash />;
        }
        return (
          <div>
            <Progress
              percent={t.progress.pct}
              size="small"
              showInfo={false}
              status={t.status === 'failed' ? 'exception' : undefined}
            />
            <div style={{ fontSize: 11 }}>
              {t.progress.done}/{t.progress.total} ({t.progress.pct}%)
            </div>
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'monitor.tasks.modal.colRequests' }),
      dataIndex: 'request_count',
      width: 64,
      align: 'center',
      render: (n: number) => <span>{n}</span>,
    },
    {
      title: '模型 / 供应商',
      key: 'model-provider',
      width: 220,
      render: (_: unknown, t: BusinessTask) => (
        <div className={styles.modelProviderCell}>
          <ModelProviderCell
            modelId={t.model_id}
            providerName={t.provider_name}
          />
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'monitor.tasks.modal.colCost' }),
      dataIndex: 'credits_cost',
      width: 90,
      align: 'right',
      render: (_: unknown, t: BusinessTask) => {
        if (!t.credits_cost || t.credits_cost <= 0) {
          return <EmptyDash />;
        }
        return (
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <ThunderboltOutlined /> {t.credits_cost.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'monitor.tasks.modal.colTime' }),
      key: 'time',
      width: 130,
      render: (_: unknown, t: BusinessTask) => (
        <div style={{ fontSize: 11, lineHeight: 1.4 }}>
          <div>{formatTime(t.started_at)}</div>
          {t.elapsed_ms > 0 && (
            <div style={{ color: 'var(--ant-color-text-tertiary)' }}>
              {formatElapsed(t.elapsed_ms)}
            </div>
          )}
        </div>
      ),
    },
  ];

  const expandedRowRender = (task: BusinessTask) => {
    const id = task.id;
    if (detailLoading[id]) {
      return (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin size="small" />
        </div>
      );
    }
    if (detailError[id]) {
      return (
        <Alert
          type="error"
          message={intl.formatMessage(
            { id: 'monitor.tasks.modal.detailFailed' },
            { error: detailError[id] },
          )}
          showIcon
        />
      );
    }
    const detail = detailMap[id];
    if (!detail) return null;
    const requests = detail.requests || [];
    if (requests.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={intl.formatMessage({
            id: 'monitor.tasks.modal.noRequests',
          })}
        />
      );
    }
    return (
      <Table<BusinessTaskRequest>
        size="small"
        rowKey="id"
        dataSource={requests}
        pagination={false}
        className={styles.compactTable}
        columns={[
          {
            title: '',
            key: 'thumb',
            width: 40,
            render: (_: unknown, r: BusinessTaskRequest) =>
              r.image_url ? (
                <img
                  src={r.image_url}
                  alt=""
                  className={styles.thumbCell}
                  loading="lazy"
                />
              ) : null,
          },
          {
            title: intl.formatMessage({
              id: 'monitor.tasks.modal.requestColLabel',
            }),
            dataIndex: 'label',
            width: 360,
            ellipsis: true,
            render: (s: string, r: BusinessTaskRequest) => (
              <div className={styles.targetCell}>
                <div className={styles.targetTitle} title={s}>
                  {s}
                </div>
                <div className={styles.targetSubTitle} title={r.kind}>
                  {r.kind}
                </div>
              </div>
            ),
          },
          {
            title: intl.formatMessage({
              id: 'monitor.tasks.modal.requestColStatus',
            }),
            dataIndex: 'status',
            width: 80,
            render: (s: string) => <Tag>{s}</Tag>,
          },
          {
            title: '模型 / 供应商',
            key: 'model-provider',
            width: 220,
            render: (_: unknown, r: BusinessTaskRequest) => (
              <div className={styles.modelProviderCell}>
                <ModelProviderCell
                  modelId={r.model_id}
                  providerName={r.provider_name}
                />
              </div>
            ),
          },
          {
            title: intl.formatMessage({ id: 'monitor.tasks.modal.colTime' }),
            key: 'time',
            width: 130,
            render: (_: unknown, r: BusinessTaskRequest) => (
              <div style={{ fontSize: 11, lineHeight: 1.4 }}>
                <div>{formatTime(r.created_at)}</div>
                {r.elapsed_ms && r.elapsed_ms > 0 ? (
                  <div style={{ color: 'var(--ant-color-text-tertiary)' }}>
                    {formatElapsed(r.elapsed_ms)}
                  </div>
                ) : null}
              </div>
            ),
          },
          {
            title: intl.formatMessage({
              id: 'monitor.tasks.modal.requestColCost',
            }),
            dataIndex: 'credits_cost',
            width: 70,
            align: 'right' as const,
            render: (n?: number) =>
              n && n > 0 ? (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <ThunderboltOutlined /> {n}
                </span>
              ) : (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--ant-color-text-tertiary)',
                  }}
                >
                  -
                </span>
              ),
          },
          {
            title: intl.formatMessage({
              id: 'monitor.tasks.modal.requestColError',
            }),
            dataIndex: 'error',
            ellipsis: true,
            render: (s?: string) =>
              s ? (
                <Tooltip title={s}>
                  <span className={styles.errorText}>{s}</span>
                </Tooltip>
              ) : null,
          },
        ]}
        scroll={{ x: 980 }}
      />
    );
  };

  return (
    <>
      <div className={styles.toolbar}>
        {!scopedUserId && (
          <Input
            size="small"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            onPressEnter={() => {
              setPagination((p) => ({ ...p, current: 1 }));
              fetchList(1, pagination.pageSize);
            }}
            allowClear
            placeholder="用户 ID"
            style={{ width: 200 }}
          />
        )}
        <Input
          size="small"
          value={taskIdFilter}
          onChange={(e) => setTaskIdFilter(e.target.value)}
          onPressEnter={() => {
            setPagination((p) => ({ ...p, current: 1 }));
            fetchList(1, pagination.pageSize);
          }}
          allowClear
          placeholder="Task ID"
          style={{ width: 220 }}
        />
        <span className={styles.toolbarLabel}>状态</span>
        <Segmented
          size="small"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v as BusinessTaskStatus | 'all');
            setPagination((p) => ({ ...p, current: 1 }));
          }}
          options={[
            {
              label: intl.formatMessage({
                id: 'monitor.tasks.modal.filterAll',
              }),
              value: 'all',
            },
            ...ALL_STATUSES.map((s) => ({
              label: intl.formatMessage({
                id: `monitor.tasks.status.${s}`,
              }),
              value: s,
            })),
          ]}
        />
        <span className={styles.toolbarLabel}>类型</span>
        <Select
          size="small"
          value={kindFilter}
          onChange={(v) => {
            setKindFilter(v as BusinessTaskKind | 'all');
            setPagination((p) => ({ ...p, current: 1 }));
          }}
          style={{ width: 160 }}
          options={[
            {
              label: intl.formatMessage({
                id: 'monitor.tasks.modal.filterAll',
              }),
              value: 'all',
            },
            ...ALL_KINDS.map((k) => ({
              label: intl.formatMessage({ id: `monitor.tasks.kind.${k}` }),
              value: k,
            })),
          ]}
        />
        <Button
          size="small"
          loading={loading}
          onClick={() => fetchList(pagination.current, pagination.pageSize)}
        >
          {intl.formatMessage({ id: 'monitor.tasks.modal.refresh' })}
        </Button>
        <div className={styles.toolbarCounts}>
          <Tag>
            {intl.formatMessage({ id: 'monitor.tasks.status.queued' })}: {counts.queued ?? 0}{' · '}
            {intl.formatMessage({ id: 'monitor.tasks.status.processing' })}:{' '}
            {counts.processing}
          </Tag>
          <Tag>
            {intl.formatMessage({ id: 'monitor.tasks.status.completed' })}:{' '}
            {counts.completed}
          </Tag>
          <Tag>
            {intl.formatMessage({ id: 'monitor.tasks.status.partial' })}:{' '}
            {counts.partial}
          </Tag>
          <Tag>
            {intl.formatMessage({ id: 'monitor.tasks.status.failed' })}:{' '}
            {counts.failed}
          </Tag>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      <div {...(fillTable ? wrapperProps : {})}>
        <Table<BusinessTask>
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={tasks}
          columns={columns}
          className={styles.compactTable}
          tableLayout="fixed"
          scroll={{
            x: 1480,
            ...(effectiveScrollY ? { y: effectiveScrollY } : {}),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: !nested,
            pageSizeOptions: ['50', '100', '200'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total}${pagination.totalCapped ? '+' : ''}`,
          }}
          onChange={(next) => {
            setPagination((p) => ({
              ...p,
              current: next.current || 1,
              pageSize: next.pageSize || p.pageSize,
            }));
          }}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => {
              setExpandedRowKeys(keys as string[]);
            },
            expandedRowRender,
            onExpand: (expanded, record) => {
              if (
                expanded &&
                !detailMap[record.id] &&
                !detailLoading[record.id]
              ) {
                fetchDetail(record);
              }
            },
          }}
        />
      </div>

      {traceTask && (
        <TraceDrawer
          open={!!traceTask}
          taskId={traceTask.id}
          kind={traceTask.kind}
          targetId={traceTask.target_id || traceTask.project_id || ''}
          onClose={() => setTraceTask(null)}
        />
      )}
    </>
  );
};

export default TaskListPanel;
